import { useCallback, useContext, useMemo, useState } from "react";
import { ApiContext } from "../../../context/MyContext";
import { useUtils } from "../../../context/UtilsContext";
import { useCachedList } from "../../../hooks/useCachedList";
import {
  SHIFT_CALL_TYPES_KEY,
  coachShiftsCache,
  serializeCoachShiftsKey,
  shiftCallTypesCache,
} from "../../../utils/listCache/caches";
import {
  fetchCoachShifts,
  fetchShiftCallTypes,
  modifyCoachShifts,
  modifyShiftCallTypes,
} from "../../../service/bookingService";
import { resolveBookingEventTypeId } from "./bookingEventType";
import { getCoachDepartmentTitles } from "./coachRoster";
import {
  buildCallTypeItems,
  buildShiftItems,
  callTypesByTier,
  callTypesForDepartment,
  localOffsetMinutes,
  planShiftRemoval,
  shiftsOnDate,
  utcShiftsToLocal,
} from "./slotGrid";

/**
 * Data layer for one coach's weekly shifts, and the call types that turn them
 * into slots.
 *
 * Replaces the per-month slot hook: since `1f1b855` nothing bookable is stored —
 * the server generates slots on every read from the coach's **weekly shifts**
 * and their **department's call types** — so this reads those two inputs
 * instead, and `slotGrid`'s `shiftSlots` mirrors the generator for the preview.
 *
 * - **Shifts** are one list per coach, not per month: a shift is a weekday +
 *   time that repeats every week **while it is in effect** (`effective_from` /
 *   `effective_to`, `be3e423`). The list is fetched with no dates, so it holds
 *   ended shifts too, and `shiftsOnDate(date)` picks what one calendar
 *   date shows — a week straddling a change of hours shows the old hours
 *   before it and the new after. Stored in UTC, converted here to local shifts
 *   (`utcShiftsToLocal`), which also rejoins a shift the backend had to split
 *   at UTC midnight.
 * - **Call types** are fetched for every department at once — token-only, a
 *   handful of rows, one shared cache entry — and narrowed to this coach's
 *   department by title (`callTypesForDepartment`).
 *
 * `employeeId` omitted means the caller's own calendar, whose department comes
 * from their own roles. Viewing somebody else's, pass `departmentTitle` (the
 * roster row carries it).
 *
 * **Writes** (`addShifts`, `deleteShift`): an ADD's `effective_from` is each
 * day's first run that hasn't ended (`nextShiftStart`; `now` if it's under
 * way), and a removal's
 * `effective_to` is the current timestamp; everything before is left as it
 * was. Removing is always a close (`planShiftRemoval`), never a DELETE, and
 * the server refuses it (409) while the shift holds any upcoming booking;
 * adding steps around the one-second rows a removed future shift leaves
 * (`buildShiftItems`, `bd2e283`). They need this service's
 * weekday ids and the "Booking" shift type id, both from `get-common-utils`;
 * the workspace already holds them through `useBookingStatuses` and passes
 * them in, rather than this hook asking for the same vocabulary a second time.
 * Without them `canWrite` is false. Whether the caller *may* write is not
 * decided here — the server does that, and the UI only hides what it knows
 * would be refused.
 *
 * @param {{ employeeId?: string, departmentTitle?: string,
 *   weekdayIdByNumber?: Map<number, string>, coachShiftTypeId?: string|null }} [params]
 */
export function useCoachShifts({
  employeeId: targetEmployeeId,
  departmentTitle,
  weekdayIdByNumber,
  coachShiftTypeId,
} = {}) {
  const { get, post, employeeData, departmentsWithRoles } = useContext(ApiContext);
  const { sessionUtils } = useUtils();
  const [saving, setSaving] = useState(false);

  // An EmployeeSchema.Employees id — NOT ApiContext.empId (a UserProfile id).
  const employeeId = targetEmployeeId || employeeData?.data?.employee_id || null;
  const eventTypeId = useMemo(
    () => resolveBookingEventTypeId(sessionUtils),
    [sessionUtils]
  );
  const configured = Boolean(employeeId && eventTypeId);

  const department = useMemo(
    () =>
      departmentTitle ||
      getCoachDepartmentTitles(employeeData, departmentsWithRoles)[0] ||
      null,
    [departmentTitle, employeeData, departmentsWithRoles]
  );

  const shiftsFetcher = useCallback(
    () =>
      fetchCoachShifts(get, { eventTypeId, employeeId }).then((payload) =>
        Array.isArray(payload?.coach_shifts) ? payload.coach_shifts : []
      ),
    [get, eventTypeId, employeeId]
  );
  const shifts = useCachedList({
    cache: coachShiftsCache,
    key: configured ? serializeCoachShiftsKey(employeeId) : null,
    fetcher: shiftsFetcher,
    enabled: configured,
  });

  const callTypes = useShiftCallTypes();

  // The browser's offset now. IST has no DST; a zone that does would shift
  // local times by an hour across the change, which is the honest reading of
  // a UTC-stored weekly shift.
  const offsetMinutes = useMemo(() => localOffsetMinutes(), []);

  const localShifts = useMemo(
    () => utcShiftsToLocal(shifts.data, offsetMinutes),
    [shifts.data, offsetMinutes]
  );

  /** The shifts one calendar date shows, sorted by start. */
  const shiftsForDate = useCallback((date) => shiftsOnDate(localShifts, date), [localShifts]);

  // Any shift still in effect now or later — "has this coach any shifts?".
  const hasCurrentShifts = useMemo(() => {
    const now = new Date();
    return localShifts.some((shift) => !shift.effectiveTo || shift.effectiveTo > now);
  }, [localShifts]);

  const { departmentId, tiers, bufferByCallTypeId } = useMemo(
    () => callTypesForDepartment(callTypes.rows, department),
    [callTypes.rows, department]
  );

  const refresh = useCallback(
    () => Promise.all([shifts.refresh(), callTypes.refresh()]),
    [shifts.refresh, callTypes.refresh] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const canWrite = Boolean(
    configured && coachShiftTypeId && weekdayIdByNumber && weekdayIdByNumber.size === 7
  );
  const writeContext = useMemo(
    () => ({ employeeId, weekdayIdByNumber, coachShiftTypeId, offsetMinutes, storedShifts: shifts.data }),
    [employeeId, weekdayIdByNumber, coachShiftTypeId, offsetMinutes, shifts.data]
  );

  /**
   * Send batches of `CoachShiftItem`s **one request each, in order**, skipping
   * empty ones, then re-read the coach's shifts.
   *
   * Separate requests so each batch stands alone (since `bd2e283` the server
   * runs a batch's UPDATEs, then DELETEs, then ADDs). A failure part-way can leave some of it applied, so the list is
   * re-read on failure too before the error propagates (with the API's words).
   *
   * Also drops the roster's all-coaches shift list, so the Coaches table's
   * "Slots this week" is not left counting the old shifts.
   *
   * `saving` stays on until the re-read lands, not just the write: the dialogs
   * validate against `localShifts`, so a button re-enabled between the two
   * would pass a second click against the old list and send the ADD again.
   */
  const write = useCallback(
    async (batches) => {
      if (!canWrite) throw new Error("Shift editing isn't available — sign out and back in, then try again.");
      const nonEmpty = batches.filter((batch) => Array.isArray(batch) && batch.length > 0);
      if (nonEmpty.length === 0) return;

      setSaving(true);
      try {
        for (const batch of nonEmpty) {
          // Sequential on purpose; see the note above.
          // eslint-disable-next-line no-await-in-loop
          await modifyCoachShifts(post, { eventTypeId, coachShifts: batch });
        }
      } finally {
        // On failure too: part of a multi-batch write may have applied.
        coachShiftsCache.remove(serializeCoachShiftsKey(null));
        await shifts.refresh().catch(() => {});
        setSaving(false);
      }
    },
    [canWrite, post, eventTypeId, shifts.refresh] // eslint-disable-line react-hooks/exhaustive-deps
  );

  /** Add one local window on each of `dayNumbers`, in effect from each day's next run. */
  const addShifts = useCallback(
    (draft) => write([buildShiftItems(draft, writeContext, new Date())]),
    [write, writeContext]
  );

  /** Remove one shift from now on, by closing it (`planShiftRemoval`). */
  const deleteShift = useCallback(
    (shift) => write(planShiftRemoval(shift, new Date())),
    [write]
  );

  return {
    employeeId,
    offsetMinutes,
    /** Every local shift, ended ones included; see `shiftsForDate`. */
    localShifts,
    shiftsForDate,
    hasCurrentShifts,
    /** Raw rows from `fetch-coach-shift`, UTC — what the writes are planned against. */
    storedShifts: shifts.data || [],
    departmentTitle: department,
    departmentId,
    /** `{ [call_no]: { id, durationMinutes, bufferMinutes } }` for this department. */
    tiers,
    hasCallTypes: Object.keys(tiers).length > 0,
    bufferByCallTypeId,
    loading: shifts.loading,
    busy: shifts.loading || shifts.isRevalidating || shifts.isRefreshing,
    hasData: shifts.data !== undefined,
    callTypesReady: callTypes.ready,
    error: shifts.error,
    callTypesError: callTypes.error,
    refresh,
    /** The vocabulary a write needs has loaded. */
    canWrite,
    saving,
    addShifts,
    deleteShift,
    hasEmployeeProfile: Boolean(employeeId),
    hasEventType: Boolean(eventTypeId),
  };
}

const NO_ROWS = [];

/**
 * Every department's call types, and the writes that change them.
 *
 * One list for the whole tenant, in one shared cache entry: the endpoint is
 * token-only and returns the same handful of rows to everybody. Bookings (the
 * Shifts panel through `useCoachShifts`, the roster's slot counts) and the
 * Call types section on Diet Utils / Workout Utils (`CallTypesSection`) all
 * read it, so an edit there shows in Bookings without a reload.
 *
 * `saveTiers(departmentId, draft)` sends only what changed — UPDATE where a
 * tier already has a row, ADD where it does not (`buildCallTypeItems`), always
 * department-wide (`plan_id: null`) and always naming the department, which an
 * executive must do. Resolves to how many rows were written (0 when nothing
 * changed, with no request). `deleteTier(id)` soft-deletes one row; bookings
 * that used it keep pointing at it. On a refusal the list is re-read before the
 * API's own error propagates.
 *
 * Who may write is the server's rule: a live `Manager` role in that
 * department, or an executive. The only screen that writes is the utils pages,
 * which are Executive-only.
 */
export function useShiftCallTypes() {
  const { get, post } = useContext(ApiContext);
  const { sessionUtils } = useUtils();
  const [saving, setSaving] = useState(false);

  const eventTypeId = useMemo(
    () => resolveBookingEventTypeId(sessionUtils),
    [sessionUtils]
  );

  const fetcher = useCallback(
    () =>
      fetchShiftCallTypes(get, { eventTypeId }).then((payload) =>
        Array.isArray(payload?.shift_call_types) ? payload.shift_call_types : []
      ),
    [get, eventTypeId]
  );
  const list = useCachedList({
    cache: shiftCallTypesCache,
    key: eventTypeId ? SHIFT_CALL_TYPES_KEY : null,
    fetcher,
    enabled: Boolean(eventTypeId),
  });
  const rows = list.data || NO_ROWS;

  const write = useCallback(
    async (items) => {
      setSaving(true);
      try {
        await modifyShiftCallTypes(post, { eventTypeId, shiftCallTypes: items });
      } catch (err) {
        await list.refresh().catch(() => {});
        throw err;
      } finally {
        setSaving(false);
      }
      await list.refresh().catch(() => {});
      return items.length;
    },
    [post, eventTypeId, list.refresh] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const saveTiers = useCallback(
    async (departmentId, draft) => {
      if (!departmentId) throw new Error("This department's id isn't known — reload and try again.");
      const items = buildCallTypeItems(departmentId, callTypesByTier(rows, departmentId), draft);
      if (items.length === 0) return 0;
      return write(items);
    },
    [rows, write]
  );

  const deleteTier = useCallback((id) => write([{ operation: "DELETE", id }]), [write]);

  return {
    rows,
    ready: list.data !== undefined,
    loading: list.loading,
    error: list.error,
    refresh: list.refresh,
    saving,
    saveTiers,
    deleteTier,
    hasEventType: Boolean(eventTypeId),
  };
}
